import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";

const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";
const WEB3FORMS_ACCESS_KEY = "8a0b6ca3-3948-40c3-8616-39064fdb29e0";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte Name, E-Mail und Nachricht ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(WEB3FORMS_ENDPOINT, {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `Neue Kontaktanfrage - ${form.name}`,
        from_name: "Solanova Website",
        name: form.name,
        email: form.email,
        phone: form.phone || "-",
        message: form.message,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Web3Forms submission failed");
      }

      toast.success("Nachricht gesendet. Wir melden uns innert 24 h.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Senden fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="section relative bg-[var(--sol-bg)] border-t hairline"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="eyebrow">Kontakt</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extralight tracking-tight mt-6 text-white leading-[1.05]">
              Sprechen wir <br />
              <span className="bronze-metallic">über Ihr Projekt.</span>
            </h2>
            <p className="font-body text-white/55 mt-8 text-base md:text-lg leading-relaxed">
              Schreiben Sie uns. Unser Team meldet sich persönlich – ohne Verkaufsdruck,
              dafür mit klaren Antworten.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href="mailto:info@solanova.ch"
                data-testid="contact-email-link"
                className="flex items-center gap-4 text-white/85 hover:text-[var(--sol-bronze-light)] transition-colors"
              >
                <span className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                  <Mail size={16} strokeWidth={1.5} />
                </span>
                <span className="font-body">info@solanova.ch</span>
              </a>
              <a
                href="tel:+41765725508"
                data-testid="contact-phone-link"
                className="flex items-center gap-4 text-white/85 hover:text-[var(--sol-bronze-light)] transition-colors"
              >
                <span className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                  <Phone size={16} strokeWidth={1.5} />
                </span>
                <span className="font-body">+41 76 572 55 08</span>
              </a>
              <div className="flex items-center gap-4 text-white/85">
                <span className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                  <MapPin size={16} strokeWidth={1.5} />
                </span>
                <span className="font-body">
                  Vogelherd 5 · 9225 Wilen-Gottshaus
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-[var(--sol-surface)] border border-white/8 rounded-3xl p-6 md:p-10 space-y-6"
            >
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                    Name *
                  </label>
                  <input
                    required
                    data-testid="contact-input-name"
                    className="sol-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Max Muster"
                  />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                    E-Mail *
                  </label>
                  <input
                    required
                    type="email"
                    data-testid="contact-input-email"
                    className="sol-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="max@beispiel.ch"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                    Telefon
                  </label>
                  <input
                    data-testid="contact-input-phone"
                    className="sol-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+41 79 000 00 00"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                  Ihre Nachricht *
                </label>
                <textarea
                  required
                  data-testid="contact-input-message"
                  className="sol-input mt-2"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Erzählen Sie uns kurz von Ihrem Vorhaben…"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <span className="text-[11px] text-white/35 font-body">
                  Wir antworten innert 24 Stunden.
                </span>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="btn-bronze rounded-full px-7 py-3.5 inline-flex items-center gap-2 text-sm font-medium disabled:opacity-60"
                >
                  {loading ? "Senden…" : "Nachricht senden"}
                  {!loading && <ArrowRight size={16} />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
